import { Capacitor, registerPlugin } from '@capacitor/core';

// ─── Screen capture blocking (Android FLAG_SECURE) ───────────────────────────
//
// Wraps WindowManager.LayoutParams.FLAG_SECURE. With it set, Android renders the
// window into a secure surface: screenshots come out black, screen recording and
// casting capture nothing, and the Recents thumbnail is blanked. It is enforced
// by the OS compositor, not by us, so no amount of JavaScript can be tricked
// into leaking a frame.
//
// WHY THIS IS SCOPED TO THE READER AND NOT SET GLOBALLY IN MainActivity
// ─────────────────────────────────────────────────────────────────────
// Capacitor is a SINGLE-ACTIVITY app: every screen — home, lessons, quizzes,
// the leaderboard, settings — is a route inside one WebView inside one Activity,
// sharing one Window. FLAG_SECURE is a property of that Window, not of a route.
// Setting it once at startup would black out screenshots of the ENTIRE app: a
// learner could no longer screenshot a grammar rule to study later, share a quiz
// score, or send us a screenshot of a bug — and Recents would show a blank card
// every time they switched apps. So the reader turns the flag on when it mounts
// and off when it unmounts, and that is the only window of time the app is
// unscreenshottable.
//
// The counter below exists because the enable/disable calls are lifecycle-driven.
// React StrictMode mounts effects twice in development, and a future second
// consumer (a preview overlay, say) could overlap with the reader; a naive
// clear-on-any-unmount would silently drop protection while a paid page was
// still on screen.

interface ScreenCapturePlugin {
  /** Sets FLAG_SECURE on the single Activity window. */
  enable(): Promise<void>;
  /** Clears FLAG_SECURE. */
  disable(): Promise<void>;
}

const ScreenCapture = registerPlugin<ScreenCapturePlugin>('ScreenCapture');

/** iOS has no FLAG_SECURE equivalent, and the browser has none at all. The paid
 *  books are Android-only, so everywhere else these are honest no-ops rather
 *  than a promise we cannot keep. */
function isSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

let secureDepth = 0;

/**
 * Blocks screenshots and screen recording until a matching
 * `disableSecureScreen()`. Nested calls are counted, not idempotent.
 *
 * Never throws: a missing native plugin (an old install, a stale `cap sync`)
 * must not stop a paying customer from reading a book they own. It degrades to
 * an unprotected reader, which is the same protection level the free ebooks
 * have always had.
 */
export async function enableSecureScreen(): Promise<void> {
  if (!isSupported()) return;
  secureDepth += 1;
  if (secureDepth > 1) return;

  try {
    await ScreenCapture.enable();
  } catch (err) {
    console.warn('[screenCapture] could not enable FLAG_SECURE', err);
  }
}

/**
 * Releases one `enableSecureScreen()` hold. The flag is only cleared when the
 * last holder lets go, so the rest of the app becomes screenshottable again the
 * moment — and not before — no protected content is on screen.
 */
export async function disableSecureScreen(): Promise<void> {
  if (!isSupported()) return;
  if (secureDepth === 0) return;
  secureDepth -= 1;
  if (secureDepth > 0) return;

  try {
    await ScreenCapture.disable();
  } catch (err) {
    console.warn('[screenCapture] could not clear FLAG_SECURE', err);
  }
}

/** True while at least one caller is holding the screen secure. Diagnostics only. */
export function isSecureScreenActive(): boolean {
  return secureDepth > 0;
}
